import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const API = '/api';

const SECTIONS = [
  { key: "subject", num: "01", label: "SUBJECT" },
  { key: "summary", num: "02", label: "SUMMARY" },
  { key: "key_points", num: "03", label: "KEY POINTS" },
  { key: "notable_lines", num: "04", label: "NOTABLE LINES" },
  { key: "statistics", num: "05", label: "STATS & DATA" },
  { key: "conclusion", num: "06", label: "CONCLUSION" },
];

export default function SummaryDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notionSaving, setNotionSaving] = useState(false);
  const [hasNotion, setHasNotion] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    Promise.all([axios.get(`${API}/summaries/${id}`), axios.get(`${API}/settings`)])
      .then(([s, set]) => { setSummary(s.data); setHasNotion(set.data.has_notion || false); })
      .catch(() => { toast.error("NOT FOUND"); navigate("/history"); })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const handleSaveToNotion = async () => {
    if (!hasNotion) { toast.error("Configure Notion in Settings"); return; }
    setNotionSaving(true);
    try {
      const res = await axios.post(`${API}/summaries/${id}/save-to-notion`);
      setSummary(prev => ({ ...prev, notion_page_id: res.data.notion_url, notion_url: res.data.notion_url }));
      toast.success("SAVED TO NOTION");
    } catch (e) { toast.error(e.response?.data?.detail || "SAVE FAILED"); }
    finally { setNotionSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm("DELETE THIS DOCUMENT?")) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/summaries/${id}`);
      toast.success("DOCUMENT DELETED");
      navigate("/history");
    } catch { toast.error("DELETE FAILED"); setDeleting(false); }
  };

  if (loading) {
    return (
      <div style={{ maxWidth: 760 }}>
        <div style={{ height: 24, background: "#eee", width: 200, marginBottom: 32 }} />
        <div style={{ border: "2px solid #111", background: "#fff", padding: 32 }}>
          {[1,2,3,4].map(i => <div key={i} style={{ height: 16, background: "#eee", marginBottom: 16, opacity: 0.5 }} />)}
        </div>
      </div>
    );
  }

  if (!summary) return null;

  const date = new Date(summary.created_at).toLocaleDateString("en-US", {
    weekday: "short", year: "numeric", month: "short", day: "numeric"
  });

  return (
    <div className="anim-in r-full" style={{ maxWidth: 820 }} data-testid="summary-detail-page">
      {/* Nav bar */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 10 }}>
        <Link
          to="/history"
          data-testid="back-to-history-link"
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: "0.62rem",
            letterSpacing: "0.14em",
            color: "#888",
            textDecoration: "none",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
          onMouseEnter={e => { e.currentTarget.style.color = "#111"; }}
          onMouseLeave={e => { e.currentTarget.style.color = "#888"; }}
        >
          ← BACK TO ARCHIVE
        </Link>

        <div style={{ display: "flex", gap: 8 }}>
          {!summary.notion_page_id ? (
            <button
              data-testid="save-notion-detail-btn"
              onClick={handleSaveToNotion}
              disabled={notionSaving || !hasNotion}
              className="ind-btn-ghost"
              style={{ fontSize: "0.62rem", padding: "6px 14px" }}
              title={!hasNotion ? "Configure Notion in Settings" : ""}
            >
              {notionSaving ? <Loader2 size={12} className="animate-spin" /> : null}
              SAVE TO NOTION
            </button>
          ) : (
            <a
              href={summary.notion_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="open-notion-detail-link"
              style={{ textDecoration: "none" }}
            >
              <button className="ind-btn-primary" style={{ fontSize: "0.62rem", padding: "6px 14px", background: "#111" }}>
                ↗ OPEN IN NOTION
              </button>
            </a>
          )}
          <button
            data-testid="delete-summary-detail-btn"
            onClick={handleDelete}
            disabled={deleting}
            style={{
              background: "transparent",
              border: "1px solid #ddd",
              cursor: "pointer",
              padding: "6px 12px",
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: "0.62rem",
              color: "#CC0000",
              transition: "all 0.1s",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = "#CC0000"; e.currentTarget.style.color = "#fff"; e.currentTarget.style.borderColor = "#CC0000"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#CC0000"; e.currentTarget.style.borderColor = "#ddd"; }}
          >
            {deleting ? <Loader2 size={12} className="animate-spin" /> : "✕ DELETE"}
          </button>
        </div>
      </div>

      {/* Document */}
      <div style={{ border: "2px solid #111", background: "#fff", position: "relative" }} data-testid="summary-paper">
        {/* Corner marks */}
        <div style={{ position: "absolute", top: -3, left: -3, width: 6, height: 6, background: "#CC0000" }} />
        <div style={{ position: "absolute", top: -3, right: -3, width: 6, height: 6, background: "#CC0000" }} />
        <div style={{ position: "absolute", bottom: -3, left: -3, width: 6, height: 6, background: "#111" }} />
        <div style={{ position: "absolute", bottom: -3, right: -3, width: 6, height: 6, background: "#111" }} />

        {/* Document header */}
        <div style={{ borderBottom: "2px solid #111", background: "#111" }}>
          {/* Thumbnail */}
          {summary.thumbnail_url && (
            <div style={{ position: "relative", height: 180, overflow: "hidden" }}>
              <img
                src={summary.thumbnail_url}
                alt=""
                style={{ width: "100%", height: "100%", objectFit: "cover", opacity: 0.5, filter: "grayscale(100%)" }}
              />
              <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.4)" }} />
            </div>
          )}
          <div style={{ padding: "20px 28px" }}>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.55rem", letterSpacing: "0.2em", color: "#888", marginBottom: 8 }}>
              DOCUMENT / {summary.channel_name.toUpperCase()} / {date.toUpperCase()}
            </div>
            <h1
              style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "1rem", fontWeight: 700, color: "#fff", letterSpacing: "0.04em", margin: 0, lineHeight: 1.4 }}
              data-testid="detail-video-title"
            >
              {summary.video_title.toUpperCase()}
            </h1>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 12 }}>
              <a
                href={summary.video_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="video-source-link"
                style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.58rem", color: "#CC0000", textDecoration: "none", letterSpacing: "0.1em" }}
              >
                ↗ VIEW SOURCE
              </a>
              {summary.notion_page_id && (
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.55rem", background: "#CC0000", color: "#fff", padding: "2px 8px", letterSpacing: "0.1em" }}>
                  ✓ NOTION
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Content sections */}
        <div style={{ padding: "0" }}>
          {SECTIONS.map((sec, idx) => {
            // Hide stats section if no stats
            if (sec.key === "statistics" && (!summary.statistics || summary.statistics.length === 0)) return null;
              if (sec.key === "notable_lines" && (!summary.notable_lines || summary.notable_lines.length === 0)) return null;
            
            return (
              <div
                key={sec.key}
                style={{ borderBottom: idx < SECTIONS.length - 1 ? "1px solid #e8e8e4" : "none", padding: "24px 28px" }}
              >
                {/* Section label */}
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.6rem", fontWeight: 700, color: "#CC0000", letterSpacing: "0.08em" }}>
                    {sec.num}
                  </span>
                  <div style={{ flex: 1, height: 1, background: "#e8e8e4" }} />
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.58rem", fontWeight: 700, letterSpacing: "0.2em", color: "#888", textTransform: "uppercase" }}>
                    {sec.label}
                  </span>
                  <div style={{ width: 5, height: 5, background: "#111" }} />
                </div>

                {sec.key === "key_points" ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }} data-testid="detail-key-points">
                    {(summary.key_points || []).map((pt, i) => (
                      <div key={i} style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.6rem", fontWeight: 700, color: "#CC0000", flexShrink: 0, marginTop: 3, letterSpacing: "0.05em" }}>
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <span style={{ fontFamily: "Inter, sans-serif", fontSize: "0.9rem", color: "#222", lineHeight: 1.65 }}>{pt}</span>
                      </div>
                    ))}
                  </div>
                ) : sec.key === "notable_lines" ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }} data-testid="detail-notable-lines">
                  {(summary.notable_lines || []).map((pt, i) => (
                    <div key={i} style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.85rem", fontWeight: 700, color: "#CC0000", flexShrink: 0, marginTop: 2 }}>❝</span>
                      <span style={{ fontFamily: "Inter, sans-serif", fontSize: "0.9rem", color: "#222", lineHeight: 1.65, fontStyle: "italic" }}>{pt}</span>
                    </div>
                  ))}
                </div>
              ) : sec.key === "statistics" ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }} data-testid="detail-statistics">
                    {(summary.statistics || []).map((pt, i) => (
                      <div key={i} style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.6rem", fontWeight: 700, color: "#111", flexShrink: 0, marginTop: 3, letterSpacing: "0.05em" }}>
                          ✦
                        </span>
                        <span style={{ fontFamily: "Inter, sans-serif", fontSize: "0.9rem", color: "#222", lineHeight: 1.65 }}>{pt}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p
                    data-testid={`detail-${sec.key === "subject" ? "subject" : sec.key === "summary" ? "summary-text" : "conclusion"}`}
                    style={{
                      fontFamily: "Inter, sans-serif",
                      fontSize: "0.9rem",
                      color: "#222",
                      lineHeight: 1.75,
                      margin: 0,
                      fontStyle: sec.key === "subject" ? "italic" : "normal",
                    }}
                  >
                    {summary[sec.key]}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {/* Doc footer */}
        <div style={{ borderTop: "2px solid #111", padding: "12px 28px", background: "#111", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.55rem", color: "#666", letterSpacing: "0.1em" }}>
            PROCESSED BY GEMINI 3 FLASH · {summary.key_points?.length || 0} KEY POINTS
          </span>
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.55rem", color: "#555", letterSpacing: "0.1em" }}>
            GIIST.NET / v1.0
          </span>
        </div>
      </div>
    </div>
  );
}
